import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import TOUR_TYPES from 'aria-viewer/tour/constants/TourTypes'
import { TOUR_TYPES as TOUR_MODULE_TYPES } from 'aria-viewer/tour/constants'

import mapSelectorsToProps from 'aria-viewer/core/selectors/mapSelectorsToProps'

import ViewInPortraitMode from 'aria-viewer/tour/components/landscape/ViewInPortraitMode'
import LandscapeView from 'aria-viewer/tour/components/landscape/LandscapeView'

import IframeModalContainer from 'aria-viewer/common/containers/IframeModal'
import { REGISTRATION_COMPLETE, REGISTRATION_EXITED } from 'aria-viewer/registration/constants/RegistrationStatus'
import { setHasRegisteredUser, setRegistrationStep } from 'aria-viewer/registration/registrationActions'
import {
    setTourLoadingStatus,
    setRegistrationClosed,
    setRegistrationEnabled,
    setRegistrationWelcomePrompt,
} from 'aria-viewer/tour/tourActions'
import { fetchPersonalizedStops } from 'aria-viewer/tour/tourActions'
import ModalContentTypes from 'aria-viewer/modal/constants/ModalContentTypes'
import { closeModal } from 'aria-viewer/modal/modalActions'
import { getEnv } from 'aria-viewer/utils/environment'
import withRetry from 'aria-viewer/utils/retry'
import qs from 'qs'
import axios from 'axios'

const BASE_URL = getEnv('REACT_APP_BASE_URL')

const withLandScapeNoRender = WrappedComponent => ({ disableLandscape, isLandscape, isRegistrationOpen, ...props }) => {
    // Disables Landscape.  Could also move InquiryFormModal into RegsitrationWrapper to make more DRY
    const disableLandscapeFlag = disableLandscape && isRegistrationOpen && isLandscape

    return disableLandscapeFlag ? (
        <LandscapeView zIndex={2005}>
            <ViewInPortraitMode message="Please rotate your device into portrait mode to continue" />
        </LandscapeView>
    ) : (
        <WrappedComponent isRegistrationOpen={isRegistrationOpen} {...props} />
    )
}

/**
 * Content Iniquiry Form Modal
 * @param {*} param
 */
const InquiryFormModal = ({
    isRegistrationOpen,
    isTourLoading,
    onClose,
    closeModal,
    setRegistrationStep,
    setTourLoadingStatus,
    setRegistrationClosed,
    setRegistrationEnabled,
    setHasRegisteredUser,
    setRegistrationWelcomePrompt,
    fetchPersonalizedStops,
    src,
    isCIFForm,
    hasBorder,
    regWelcomePrompt,
    uriParams,
}) => {
    return isRegistrationOpen ? (
        <IframeModalContainer
            onReady={_ => {
                if (isTourLoading) {
                    setTourLoadingStatus(false)
                }
            }}
            onComplete={millisecondsUntilClose => {
                if (isTourLoading) {
                    setTourLoadingStatus(false)
                }
                setRegistrationEnabled(false, REGISTRATION_COMPLETE)
                setHasRegisteredUser(true)
                if (regWelcomePrompt) setRegistrationWelcomePrompt(false)
                setTimeout(() => {
                    closeModal()
                }, millisecondsUntilClose)
            }}
            onDismiss={(completed = false) => {
                closeModal()
                setTourLoadingStatus(false)
                if (!isCIFForm || completed) {
                    setRegistrationEnabled(false)
                }
                if (regWelcomePrompt) setRegistrationWelcomePrompt(false)
                setRegistrationClosed(REGISTRATION_EXITED)
                onClose(completed)
            }}
            onReceiveMessage={message => {
                if (message.indexOf('data:::stepData') !== -1) {
                    const r = message.split(':::')
                    const stepData = JSON.parse(r[2])
                    setRegistrationStep(stepData)
                }
                if (message.indexOf(':::personalization') !== -1) {
                    const { inst_id: instId, loc_id: locId, tourid, userkey } = uriParams
                    withRetry({
                        retries: 6,
                        delay: 500,
                        backOffCoefficient: 2,
                        fn: _ => {
                            return new Promise((resolve, reject) => {
                                axios
                                    .get(
                                        `${BASE_URL}/api/v2/institutions/${instId}/locations/${locId}/personalized-stops/me/status`,
                                        { params: { userkey, tour_id: tourid } }
                                    )
                                    .then(response => {
                                        const { live_updates: liveUpdates } = response.data.resources
                                        const completedUpdate = liveUpdates.some(
                                            step => step.completed_ranking === true
                                        )
                                        if (completedUpdate) {
                                            resolve()
                                        } else {
                                            reject()
                                        }
                                    })
                                    .catch(error => {
                                        reject(error)
                                    })
                            })
                        },
                        onSuccess: _ => {
                            fetchPersonalizedStops(instId, locId, tourid, userkey)
                        },
                    })
                }
            }}
            hasBorder={hasBorder}
            delayCloseButtonUntilOnReady={true}
            completeMessage="RegistrationForm_Completed"
            readyMessage="cif:RegistrationForm_Ready"
            src={src}
            uriParams={uriParams}
        />
    ) : null
}

InquiryFormModal.defaultProps = {
    embed: 1,
    hasBorder: false,
    disableLandscape: false,
    isCIFForm: true, //InquiryFormModal is always CIF
    onClose: completed => {},
}

InquiryFormModal.propTypes = {
    embed: PropTypes.number,
    hasBorder: PropTypes.bool,
    isCIFForm: PropTypes.bool,
    disableLandscape: PropTypes.bool,
    onClose: PropTypes.func,
}

export default compose(
    connect(
        state => {
            const { tour, location, originalPlatform, platform, regWelcomePrompt } = mapSelectorsToProps([
                'tour',
                'location',
                'originalPlatform',
                'platform',
                'regWelcomePrompt',
            ])()(state)

            const isAppilyForm = state.getIn(['registration', 'formType']) == 'appily'

            const userkey = state.getIn(['analytics', 'userkey'])
            const sessionId = state.getIn(['analytics', 'sessionId'])
            const token = state.getIn(['analytics', 'userToken'])
            const isIWC = state.getIn(['device', 'useResponsiveUI'])

            const query = qs.parse(state.get('routing').location.search, { ignoreQueryPrefix: true })

            const uriParams = {
                ...query,
                inst_id: location.owner_id,
                loc_id: location.loc_id,
                tourid: tour.tourid,
                userkey: userkey,
                source: platform,
                original_source: originalPlatform,
                sessionid: sessionId,
                embed: 1,
                embed_type: isIWC ? TOUR_TYPES.TOUR_TYPE_IWC : TOUR_TYPES.TOUR_TYPE_VIRTUAL_TOUR,
                typeid: location.typeid,
                token: token,
                module_id: TOUR_MODULE_TYPES.walkingtour,
            }

            if (isAppilyForm) uriParams.mode = 'appily'

            const queryStr = qs.stringify(uriParams, { addQueryPrefix: true })
            const cifURL =
                getEnv('REACT_APP_CONTENT_INQUIRY_FORM_URL') +
                '#/' +
                location.owner_id +
                '/' +
                location.loc_id +
                queryStr

            return {
                src: cifURL,
                isLandscape: state.getIn(['device', 'isLandscape']),
                isRegistrationOpen: state
                    .getIn(['modal', 'modalStack'])
                    .some(modal =>
                        [ModalContentTypes.WELCOME_REGISTRATION, ModalContentTypes.REGISTRATION].includes(
                            modal.content.type
                        )
                    ),
                isTourLoading: state.getIn(['tour', 'loading', 'tour']),
                regWelcomePrompt,
                uriParams,
            }
        },
        {
            closeModal,
            setRegistrationStep,
            setRegistrationClosed,
            setRegistrationEnabled,
            setHasRegisteredUser,
            setTourLoadingStatus,
            setRegistrationWelcomePrompt,
            fetchPersonalizedStops,
        }
    ),
    withLandScapeNoRender
)(InquiryFormModal)
